import { getDataValue } from '@juvare/json-data-util';
import { getErrorMessage } from '@juvare/widget-ui/Utils';
import cloneDeep from 'lodash/cloneDeep';
import { WidgetHandler } from '../../WidgetHandler';
import { subFormProviderCache } from '../../subFormProviderCache';
import { applyRules } from './applyRules';
import { callConfigFunction } from './callConfigFunction';
import { findSubFormProvider } from './findSubFormProvider';
import { renderForm } from './renderForm';
/**
 * Loads a sub-form if necessary, based on configuration and internal state.
 * Triggers sub-form provider lookup, rule application, and re-renders the form.
 *
 * @template T - The sub-form widget configuration type.
 * @param {WidgetProps<T>} props - The widget props containing sub-form configuration and state.
 */
export function loadSubFormIfNeeded(props) {
    var _a;
    if (needToLoadSubForm(props)) {
        if (props.config.subFormProvider) {
            const wasSubFormLoaded = !!props.internalData.subForm;
            const applySubForm = (subForm) => {
                var _a, _b;
                props.internalData.loading = false;
                if (props.config.clearDataOnSubFormChange && wasSubFormLoaded) {
                    props.setData(undefined);
                }
                props.internalData.subForm = subForm;
                delete props.internalData.error;
                for (const childConfig of subForm.children) {
                    applyRules(childConfig, props.widgets, props.rootData, (_a = props.rootParams) === null || _a === void 0 ? void 0 : _a.formParams, (_b = props.rootParams) === null || _b === void 0 ? void 0 : _b.rules);
                }
                props.recreateChildProps();
            };
            const loadSubForm = (subFormPromise) => {
                props.internalData.loading = true;
                delete props.internalData.subForm;
                delete props.internalData.error;
                props.update();
                subFormPromise
                    .then((subForm) => {
                    applySubForm(cloneDeep(subForm));
                    renderForm(props, { changed: wasSubFormLoaded });
                })
                    .catch((error) => {
                    console.error('Failed to load sub-form', error);
                    props.internalData.loading = false;
                    delete props.internalData.subForm;
                    props.internalData.error = getErrorMessage(error);
                    props.recreateChildProps();
                    renderForm(props);
                });
            };
            if (props.config.subFormProvider.type) {
                const subFormProvider = findSubFormProvider(props, props.config.subFormProvider.type);
                if (subFormProvider) {
                    let params = props.config.subFormProvider.params;
                    if (params == null) {
                        if (props.config.subFormProvider.paramsFrom) {
                            params = (_a = new WidgetHandler(props).select(props.config.subFormProvider.paramsFrom)) === null || _a === void 0 ? void 0 : _a.getData();
                        }
                        else if (props.config.subFormProvider.paramsFromFormData) {
                            params = getDataValue(props.rootData, props.config.subFormProvider.paramsFromFormData);
                        }
                        else if (props.config.subFormProvider.paramsFromFormParams) {
                            params = getDataValue(props.rootParams.formParams, props.config.subFormProvider.paramsFromFormParams);
                        }
                    }
                    const subForm = subFormProviderCache.getCachedValue(subFormProvider.type, params);
                    if (subForm) {
                        applySubForm(cloneDeep(subForm));
                        renderForm(props);
                    }
                    else {
                        loadSubForm(subFormProviderCache.getValue(subFormProvider, params));
                    }
                }
                else {
                    console.error('Sub-form provider not found, type: ' + props.config.subFormProvider.type);
                    props.internalData.loading = false;
                    delete props.internalData.subForm;
                    props.internalData.error = 'Sub-form provider not found';
                    props.recreateChildProps();
                }
            }
            else if (props.config.subFormProvider.custom) {
                loadSubForm(callConfigFunction(props.config.subFormProvider.custom, 'subFormProvider custom', 'handler', new WidgetHandler(props)));
            }
        }
        else {
            props.internalData.loading = false;
            delete props.internalData.subForm;
            delete props.internalData.error;
            props.recreateChildProps();
        }
    }
}
function needToLoadSubForm(props) {
    var _a;
    let check;
    if (props.config.subFormProvider) {
        if (props.config.subFormProvider.type) {
            let params = props.config.subFormProvider.params;
            if (params == null) {
                if (props.config.subFormProvider.paramsFrom) {
                    params = (_a = new WidgetHandler(props).select(props.config.subFormProvider.paramsFrom)) === null || _a === void 0 ? void 0 : _a.getData();
                }
                else if (props.config.subFormProvider.paramsFromFormData) {
                    params = getDataValue(props.rootData, props.config.subFormProvider.paramsFromFormData);
                }
                else if (props.config.subFormProvider.paramsFromFormParams) {
                    params = getDataValue(props.rootParams.formParams, props.config.subFormProvider.paramsFromFormParams);
                }
            }
            check = JSON.stringify({ type: props.config.subFormProvider.type, params: params });
        }
        else if (props.config.subFormProvider.custom) {
            check = 'custom';
        }
        else {
            check = 'unknown';
        }
    }
    else {
        check = 'none';
    }
    if (props.internalData.subFormProviderCheck !== check || props.internalData.loadSubForm) {
        props.internalData.subFormProviderCheck = check;
        delete props.internalData.loadSubForm;
        return true;
    }
    else {
        return false;
    }
}
